import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/joy";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import { useAuthentication } from "../../hooks/query";
import { useAuth, useSearchParams } from "../../hooks";
import { axiosPrivate } from "../../service";
import { Button } from "../../shared";
import { NoOrganizationAccess } from "../";
import { saveAuthSession } from "../../utils/offlineAuthSession";

const SELECTED_ORG_STORAGE_KEY = "selectedOrganizationId";

function resolveSelectedOrganization(organizations: any[]) {
    if (!organizations.length) return null;
    const savedId = sessionStorage.getItem(SELECTED_ORG_STORAGE_KEY);
    return (
        organizations.find((org) => org.Id === savedId) ||
        organizations[0] ||
        null
    );
}

export default function AuthCallback() {
    const searchParams = useSearchParams();
    const navigate = useNavigate();
    const {
        setIsAuthenticated,
        setUser,
        setToken,
        setRoles,
        setOrganizations,
        isAuthenticated,
    } = useAuth();
    const hasProcessed = useRef(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [noOrganization, setNoOrganization] = useState(false);

    const { useCodeExchange } = useAuthentication();
    const codeExchangeSvc = useCodeExchange();

    const fetchData = async () => {
        if (hasProcessed.current || isAuthenticated) {
            return;
        }

        try {
            const code = searchParams.get("code");
            if (!code) {
                setErrorMessage(
                    "Missing login code. Please try signing in again.",
                );
                return;
            }

            hasProcessed.current = true;
            const response = await codeExchangeSvc.mutateAsync({
                Code: searchParams.get("code"),
                State: searchParams.get("state"),
            });

            const accessToken = response.AccessToken;
            if (!accessToken) {
                throw new Error("No access token returned from login.");
            }

            axiosPrivate.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
            setToken(accessToken);

            const organizations = Array.isArray(response.Organizations)
                ? response.Organizations
                : [];
            const roles = Array.isArray(response.Roles) ? response.Roles : [];

            if (!organizations.length) {
                setNoOrganization(true);
                return;
            }

            const selected = resolveSelectedOrganization(organizations);
            if (selected?.Id) {
                sessionStorage.setItem(SELECTED_ORG_STORAGE_KEY, selected.Id);
            }

            setRoles(roles);
            setOrganizations(organizations);
            setUser({ Id: response.UserId, user: {}, roles });
            setIsAuthenticated(true);
            await saveAuthSession({
                token: accessToken,
                user: { Id: response.UserId, user: {}, roles },
                roles,
                organizations,
                selectedOrganizationId: selected?.Id || null,
            });
        } catch (error: any) {
            console.error(error);
            hasProcessed.current = false;
            const message =
                error?.response?.data?.Error ||
                error?.message ||
                "Sign-in failed. Please try again.";
            setErrorMessage(
                typeof message === "string"
                    ? message
                    : "Sign-in failed. Please try again.",
            );
        }
    };

    useEffect(() => {
        fetchData().then();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isAuthenticated) {
            navigate("/");
        }
    }, [isAuthenticated, navigate]);

    if (noOrganization) {
        return (
            <NoOrganizationAccess
                onBackToLogin={() => navigate("/auth/landing")}
            />
        );
    }

    if (errorMessage) {
        return (
            <Box
                display={"flex"}
                flexDirection={"column"}
                alignItems={"center"}
                justifyContent={"center"}
                height={"100%"}
                gap={1}
                px={2}
                textAlign={"center"}
            >
                <Typography level="title-md">
                    Couldn’t finish signing in
                </Typography>
                <Typography level="body-sm" color="danger">
                    {errorMessage}
                </Typography>
                <Button
                    endDecorator={<ChevronRight size={16} />}
                    onClick={() => navigate("/auth/landing")}
                >
                    Back to login
                </Button>
            </Box>
        );
    }

    return (
        <Box
            display={"flex"}
            flexDirection={"column"}
            alignItems={"center"}
            justifyContent={"center"}
            height={"100%"}
        >
            <CircularProgress size="sm" sx={{ mb: 2 }} />
            <Typography level="body-md">
                Preparing your work site...{" "}
            </Typography>
        </Box>
    );
}
